import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import {
  FaFileContract,
  FaGavel,
  FaExclamationTriangle,
  FaCheckCircle,
} from "react-icons/fa";
import bgImage from "../assets/bg.png";

const TermsContainer = styled.div`
  min-height: 100vh;
`;

const HeroSection = styled.section`
  min-height: 100vh;
  background: url(${bgImage}) center/cover no-repeat;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
`;

const BackgroundPattern = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image: radial-gradient(
      circle at 20% 80%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 80% 20%,
      rgba(255, 255, 255, 0.1) 0%,
      transparent 50%
    );
  z-index: 1;
`;

const HeroContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
  text-align: center;
  color: white;
  z-index: 2;
  position: relative;
`;

const HeroTitle = styled(motion.h1)`
  font-size: 4rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 2.5rem;
  }
`;

const HeroSubtitle = styled(motion.p)`
  font-size: 1.5rem;
  margin-bottom: 2rem;
  opacity: 0.9;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const ContentSection = styled.section`
  padding: 4rem 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);

  @media (max-width: 768px) {
    padding: 3rem 0;
  }

  @media (max-width: 480px) {
    padding: 2rem 0;
  }
`;

const Container = styled.div`
  max-width: 800px;
  margin: 0 auto;
  padding: 0 2rem;

  @media (max-width: 480px) {
    padding: 0 1rem;
  }
`;

const Content = styled(motion.div)`
  background: white;
  border-radius: 15px;
  padding: 3rem;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    padding: 2rem;
  }

  @media (max-width: 480px) {
    padding: 1.5rem;
    border-radius: 10px;
  }
`;

const Section = styled.div`
  margin-bottom: 2.5rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #005d3b;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;

  .icon {
    margin-right: 0.5rem;
    font-size: 1.2rem;
  }
`;

const SectionContent = styled.div`
  color: #333;
  line-height: 1.8;

  p {
    margin-bottom: 1rem;
  }

  ul {
    margin: 1rem 0;
    padding-left: 2rem;

    li {
      margin-bottom: 0.5rem;
    }
  }

  strong {
    color: #005d3b;
    font-weight: 600;
  }

  ol {
    margin: 1rem 0;
    padding-left: 2rem;

    li {
      margin-bottom: 0.5rem;
    }
  }
`;

const LastUpdated = styled.div`
  text-align: center;
  color: #666;
  font-style: italic;
  margin-top: 2rem;
`;

const TermsAndConditions = () => {
  return (
    <TermsContainer>
      <HeroSection id="terms-hero">
        <BackgroundPattern />
        <HeroContent>
          <HeroTitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            Terms and Conditions
          </HeroTitle>

          <HeroSubtitle
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Please read these terms carefully before using our services. By
            using WATA, you agree to be bound by these terms.
          </HeroSubtitle>
        </HeroContent>
      </HeroSection>

      <ContentSection>
        <Container>
          <Content
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Section>
              <SectionTitle>
                <FaFileContract className="icon" />
                Acceptance of Terms
              </SectionTitle>
              <SectionContent>
                <p>
                  By accessing and using WATA (We Are The Answer) services, you
                  accept and agree to be bound by the terms and provision of
                  this agreement. If you do not agree to abide by the above,
                  please do not use this service.
                </p>
                <p>
                  These terms apply to all visitors, users, and others who
                  access or use our platform and services.
                </p>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaCheckCircle className="icon" />
                Description of Service
              </SectionTitle>
              <SectionContent>
                <p>
                  WATA provides digital solutions for community building,
                  accountability, and social impact. Our services include:
                </p>
                <ul>
                  <li>Community engagement platforms</li>
                  <li>Voice and dialogue facilitation tools</li>
                  <li>Accountability tracking systems</li>
                  <li>Impact analytics and reporting</li>
                  <li>Educational resources and training</li>
                </ul>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaGavel className="icon" />
                User Responsibilities
              </SectionTitle>
              <SectionContent>
                <p>As a user of our platform, you agree to:</p>
                <ol>
                  <li>
                    <strong>Provide accurate information:</strong> All
                    information you provide must be truthful and up-to-date
                  </li>
                  <li>
                    <strong>Respect others:</strong> Treat all community members
                    with respect and dignity
                  </li>
                  <li>
                    <strong>Follow community guidelines:</strong> Adhere to our
                    community standards and policies
                  </li>
                  <li>
                    <strong>Use services appropriately:</strong> Use our
                    platform for its intended purpose of community building and
                    social impact
                  </li>
                  <li>
                    <strong>Maintain account security:</strong> Keep your login
                    credentials secure and notify us of any unauthorized access
                  </li>
                </ol>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaExclamationTriangle className="icon" />
                Prohibited Activities
              </SectionTitle>
              <SectionContent>
                <p>You may not use our services to:</p>
                <ul>
                  <li>Violate any laws or regulations</li>
                  <li>Harass, threaten, or intimidate others</li>
                  <li>Share false, misleading, or harmful content</li>
                  <li>Spam or send unsolicited communications</li>
                  <li>Attempt to gain unauthorized access to our systems</li>
                  <li>Interfere with the proper functioning of our platform</li>
                  <li>
                    Use our services for commercial purposes without permission
                  </li>
                  <li>
                    Share content that infringes on intellectual property rights
                  </li>
                </ul>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaFileContract className="icon" />
                Intellectual Property Rights
              </SectionTitle>
              <SectionContent>
                <p>
                  The content, organization, graphics, design, compilation,
                  magnetic translation, digital conversion, and other matters
                  related to our platform are protected under applicable
                  copyrights, trademarks, and other proprietary rights.
                </p>
                <p>
                  You retain ownership of content you create and share on our
                  platform, but grant us a license to use, display, and
                  distribute such content in connection with our services.
                </p>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaExclamationTriangle className="icon" />
                Limitation of Liability
              </SectionTitle>
              <SectionContent>
                <p>
                  WATA shall not be liable for any indirect, incidental,
                  special, consequential, or punitive damages, including without
                  limitation, loss of profits, data, use, goodwill, or other
                  intangible losses, resulting from your use of our services.
                </p>
                <p>
                  Our total liability to you for any damages shall not exceed
                  the amount you paid us for the services in the 12 months
                  preceding the claim.
                </p>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaGavel className="icon" />
                Disclaimers
              </SectionTitle>
              <SectionContent>
                <p>
                  Our services are provided "as is" and "as available" without
                  warranties of any kind, either express or implied. We disclaim
                  all warranties, including but not limited to:
                </p>
                <ul>
                  <li>Merchantability and fitness for a particular purpose</li>
                  <li>Non-infringement of third-party rights</li>
                  <li>Uninterrupted or error-free service</li>
                  <li>Security of data transmission</li>
                </ul>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaCheckCircle className="icon" />
                Account Termination
              </SectionTitle>
              <SectionContent>
                <p>
                  We may terminate or suspend your account immediately, without
                  prior notice or liability, for any reason whatsoever,
                  including without limitation if you breach the terms.
                </p>
                <p>
                  Upon termination, your right to use the service will cease
                  immediately. You may also terminate your account at any time
                  by contacting us.
                </p>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaFileContract className="icon" />
                Governing Law
              </SectionTitle>
              <SectionContent>
                <p>
                  These terms shall be interpreted and governed by the laws of
                  the jurisdiction in which WATA operates, without regard to its
                  conflict of law provisions.
                </p>
                <p>
                  Any disputes arising from these terms or your use of our
                  services shall be resolved through binding arbitration or in
                  the courts of competent jurisdiction.
                </p>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaExclamationTriangle className="icon" />
                Changes to Terms
              </SectionTitle>
              <SectionContent>
                <p>
                  We reserve the right to modify or replace these terms at any
                  time. If a revision is material, we will try to provide at
                  least 30 days notice prior to any new terms taking effect.
                </p>
                <p>
                  Your continued use of our services after any changes
                  constitutes acceptance of the new terms.
                </p>
              </SectionContent>
            </Section>

            <Section>
              <SectionTitle>
                <FaCheckCircle className="icon" />
                Contact Information
              </SectionTitle>
              <SectionContent>
                <p>
                  If you have any questions about these terms and conditions,
                  please contact us:
                </p>
                <ul>
                  <li>
                    <strong>Email:</strong> info@wata-africa.com
                  </li>
                  <li>
                    <strong>Phone:</strong> +256 705813875
                  </li>
                  <li>
                    <strong>Address:</strong> 123 Water Tech Ave, Innovation
                    City
                  </li>
                </ul>
              </SectionContent>
            </Section>
          </Content>

          <LastUpdated>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              Last Updated: {new Date().toLocaleDateString()}
            </motion.p>
          </LastUpdated>
        </Container>
      </ContentSection>
    </TermsContainer>
  );
};

export default TermsAndConditions;
